import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GAME_CONFIG } from '../config/gameConfig';
import { RHYTHM_CONFIG } from '../config/RhythmConfig';
import { MUSIC_BPM } from '../config/beatmaps';
import { Timestamp } from 'firebase/firestore';
import { toKstIso } from '../utils/firestoreApi';

export const useRhythmGameLogic = ({
  onGameEnd,
  currentTimeRef,
  gameMode = 'easy',
  currentRound = 1,
  totalRounds = 1,
  music = RHYTHM_CONFIG.RHYTHM_MUSIC,
  beatmap,
  shouldPreloadAudio,
  onAudioPreloaded,
  audioController,
  rhythmConfig = RHYTHM_CONFIG
}) => {
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [showCombo, setShowCombo] = useState(false);
  const [audioReady, setAudioReady] = useState(false);

  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const gameAreaRef = useRef(null);
  const judgeTimeout = useRef(null);
  const hitNotesRef = useRef(new Set());
  const missedNotesRef = useRef(new Set());
  const comboRef = useRef(0);
  const [canvasWidth, setCanvasWidth] = useState(400);
  const [canvasHeight, setCanvasHeight] = useState(640);
  const scoreRef = useRef(0);
  const maxComboRef = useRef(0);
  const activeTouchesRef = useRef(new Map());
  const groupedNotesRef = useRef({});
  const groupTouchedLanesRef = useRef({});
  const groupTimeoutsRef = useRef({});
  const laneQueuesRef = useRef({});
  const perfectGroupsRef = useRef(0);

  // Config에서 값 가져오기
  const NOTE_TRAVEL_TIME = rhythmConfig.calculateNoteTravelTime(MUSIC_BPM);
  const JUDGMENT_WINDOWS = rhythmConfig.JUDGMENT_WINDOWS;
  const ROUND_MAX_SCORES = rhythmConfig.ROUND_MAX_SCORES;

  // 현재 게임 모드에 따른 판정 윈도우 적용
  const { perfect, good, miss } = JUDGMENT_WINDOWS[gameMode];

  const PERFECT_WINDOW = perfect;
  const GOOD_WINDOW = good;
  const MISS_WINDOW = miss;

  const backgroundMusic = music; // music은 이미 선택된 음악 파일 경로
  const currentBeatmap = beatmap;

  // 그룹별 노트 초기화 (게임 시작 시)
  useEffect(() => {
    const map = {};
    currentBeatmap.forEach(note => {
      const key = note.time;
      if (!map[key]) map[key] = [];
      map[key].push(note);
    });
    groupedNotesRef.current = map;
  }, [currentBeatmap]);

  // 라운드별 최대 점수
  const maxScoreForRound = ROUND_MAX_SCORES[gameMode] || 3000;
  const groupTimes = React.useMemo(() => Array.from(new Set(currentBeatmap.map(note => note.time))), [currentBeatmap]);
  const groupCount = groupTimes.length;

  // baseScore를 그룹 개수와 콤보 보너스를 고려해서 역산
  let sum = 0;
  for (let combo = 1; combo <= groupCount; combo++) {
    sum += (1 + combo / 100);
  }
  const baseScore = maxScoreForRound / sum;

  // 레인별 큐 초기화
  useEffect(() => {
    const queues = { 0: [], 1: [], 2: [], 3: [] };
    currentBeatmap.forEach(note => {
      queues[note.lane].push(note.time);
    });
    Object.values(queues).forEach(arr => arr.sort((a, b) => a - b));
    laneQueuesRef.current = queues;
  }, [currentBeatmap]);

  // 캔버스 크기 동기화 (부드러운 렌더링을 위한 최적화)
  useEffect(() => {
    const updateCanvasSize = () => {
      if (!gameAreaRef.current || !canvasRef.current) return;
      
      const dpr = Math.min(window.devicePixelRatio || 1, 2); // 최대 2배로 제한하여 성능 최적화
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // 실제 게임 영역의 크기를 측정
        const rect = gameAreaRef.current.getBoundingClientRect();
        const width = Math.floor(rect.width);
        const height = Math.floor(rect.height);
        
        // 캔버스 크기 설정
        canvasRef.current.width = width * dpr;
        canvasRef.current.height = height * dpr;
        canvasRef.current.style.width = `${width}px`;
        canvasRef.current.style.height = `${height}px`;
        
        // 컨텍스트 설정
        const ctx = canvasRef.current.getContext('2d', { 
          alpha: true,
          antialias: true,
          desynchronized: false
        });
        
        // DPR 스케일링
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        
        // 렌더링 품질 설정
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        setCanvasWidth(width);
        setCanvasHeight(height);
      } else {
        const rect = gameAreaRef.current.getBoundingClientRect();
        const width = Math.floor(rect.width);
        const height = Math.floor(rect.height);
        
        // 캔버스 크기 설정
        canvasRef.current.width = width * dpr;
        canvasRef.current.height = height * dpr;
        canvasRef.current.style.width = `${width}px`;
        canvasRef.current.style.height = `${height}px`;
        
        // 컨텍스트 설정
        const ctx = canvasRef.current.getContext('2d', { 
          alpha: true,
          antialias: true,
          desynchronized: false
        });
        
        // DPR 스케일링
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        
        // 렌더링 품질 설정
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        setCanvasWidth(width);
        setCanvasHeight(height);
      }
    };

    // 초기 설정
    updateCanvasSize();
    
    // 리사이즈 이벤트 처리 (디바운싱 적용)
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateCanvasSize, 100);
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // 게임이 끝나면 음악 멈추기, 다음 라운드 준비
  useEffect(() => {
    if (showResult) {
      audioController.reset();
    } else {
      hitNotesRef.current.clear();
      missedNotesRef.current.clear();
      setScore(0);
      setCombo(0);
      setMaxCombo(0);
      comboRef.current = 0;
      setShowCombo(false);
    }
  }, [showResult]);

  // 브라우저를 벗어나면 해당 라운드 종료
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && !showResult) {
        audioController.stop();
        onGameEnd({
          score: scoreRef.current,
          combo: maxComboRef.current,
          timestamp: toKstIso(Timestamp.now())
        });
        setShowResult(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handleVisibility);
    window.addEventListener('beforeunload', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handleVisibility);
      window.removeEventListener('beforeunload', handleVisibility);
    };
  }, [showResult]);

  // 게임 결과가 표시될 때 콤보 숨기기
  useEffect(() => {
    if (showResult) {
      setShowCombo(false);
    }
  }, [showResult]);

  return {
    // 상태
    score,
    setScore,
    combo,
    setCombo,
    maxCombo,
    setMaxCombo,
    showResult,
    setShowResult,
    showCombo,
    setShowCombo,
    audioReady,
    setAudioReady,
    canvasWidth,
    setCanvasWidth,
    canvasHeight,
    setCanvasHeight,

    // refs
    canvasRef,
    animationRef,
    gameAreaRef,
    judgeTimeout,
    hitNotesRef,
    missedNotesRef,
    comboRef,
    scoreRef,
    maxComboRef,
    activeTouchesRef,
    groupedNotesRef,
    groupTouchedLanesRef,
    groupTimeoutsRef,
    laneQueuesRef,
    perfectGroupsRef,
    currentTimeRef,

    // 상수
    NOTE_TRAVEL_TIME,
    PERFECT_WINDOW,
    GOOD_WINDOW,
    MISS_WINDOW,
    backgroundMusic,
    currentBeatmap,
    maxScoreForRound,
    groupCount,
    baseScore,
    currentRound,
    totalRounds,
    gameMode
  };
};
